const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_WIDTH_COOKIE_NAME = "sidebar_width";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

/** Default expanded width, in px (16rem). */
export const SIDEBAR_WIDTH_DEFAULT_PX = 256;
/** Narrow enough to be useful, wide enough that labels still read. */
export const SIDEBAR_WIDTH_MIN_PX = 180;
/** Past this the sidebar starts eating the page it exists to navigate. */
export const SIDEBAR_WIDTH_MAX_PX = 520;

/**
 * Keep a requested width inside the usable range.
 *
 * Pure and exported so the drag handler, the cookie read, and the tests all agree on
 * one rule — a persisted width from an older/wider build must never be able to render
 * a sidebar nobody can drag back.
 */
export function clampSidebarWidth(px: number): number {
  if (!Number.isFinite(px)) return SIDEBAR_WIDTH_DEFAULT_PX;
  return Math.min(
    SIDEBAR_WIDTH_MAX_PX,
    Math.max(SIDEBAR_WIDTH_MIN_PX, Math.round(px)),
  );
}

/** Last desktop open/collapsed choice written by SidebarProvider. */
export function readSidebarOpenFromCookie(defaultOpen = true): boolean {
  if (typeof document === "undefined") return defaultOpen;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${SIDEBAR_COOKIE_NAME}=`));
  if (!match) return defaultOpen;
  const value = match.slice(SIDEBAR_COOKIE_NAME.length + 1);
  if (value === "true") return true;
  if (value === "false") return false;
  return defaultOpen;
}

export function writeSidebarOpenCookie(open: boolean): void {
  if (typeof document === "undefined") return;
  document.cookie = `${SIDEBAR_COOKIE_NAME}=${open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
}

/** Last width the user dragged the sidebar to, in px. */
export function readSidebarWidthFromCookie(
  defaultWidth = SIDEBAR_WIDTH_DEFAULT_PX,
): number {
  if (typeof document === "undefined") return defaultWidth;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${SIDEBAR_WIDTH_COOKIE_NAME}=`));
  if (!match) return defaultWidth;
  const parsed = Number(match.slice(SIDEBAR_WIDTH_COOKIE_NAME.length + 1));
  return Number.isFinite(parsed) ? clampSidebarWidth(parsed) : defaultWidth;
}

export function writeSidebarWidthCookie(px: number): void {
  if (typeof document === "undefined") return;
  document.cookie = `${SIDEBAR_WIDTH_COOKIE_NAME}=${clampSidebarWidth(px)}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
}
